import { useContext, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router';
import { CommonContext, ProgressContext } from '../../../../../contexts';
import { createDiscipleType, deleteDiscipleType, getDiscipleTypes, updateDiscipleType } from '../apis/disciple-apis';
import { DiscipleType } from '../types/disciple';
import { DiscipleTypeCreate } from './DiscipleTypeCreate';
import { DiscipleTypeList } from './DiscipleTypeList';
import styles from './AdminYCMS.module.css';

const AdminYCMS = () => {
  const { categories } = useContext(CommonContext);
  const { spinner } = useContext(ProgressContext);
  const { pathname } = useLocation();

  const [types, setTypes] = useState<DiscipleType[]>([]);
  const [newType, setNewType] = useState<DiscipleType>({ id: 0, departmentId: 0, departmentName: '', displayName: '', description: '', target: undefined });
  const [highlightedId, setHighlightedId] = useState<number | null>(null);

  /**
   * [시작] 제자종류 목록 조회
   */
  useEffect(() => {
    getDiscipleTypes().then(setTypes);
  }, []);

  // 하이라이트 자동 제거 기능 제거 (사용자 요청)

  /**
   * 등록
   */
  const handleAddType = async () => {
    try {
      spinner.start();
      const createdItem = await createDiscipleType({
        departmentId: newType.departmentId ?? 0,
        displayName: newType.displayName,
        description: newType.description,
        target: newType.target,
      });

      // DB에서 전체 리스트 다시 가져오기 (DB 순서대로 표시)
      const updatedTypes = await getDiscipleTypes();
      setTypes(updatedTypes);

      // 추가된 항목 하이라이트 (createDiscipleType의 반환값 사용)
      if (createdItem && createdItem.id) {
        setTimeout(() => {
          setHighlightedId(createdItem.id);
        }, 100);
      }

      setNewType({ id: 0, departmentId: 0, departmentName: '', displayName: '', description: '', target: undefined });
      toast.success('등록되었습니다.');
    } catch (error) {
      console.error(error);
      toast.error(error.message || '등록에 실패했습니다.');
    } finally {
      spinner.stop();
    }
  };

  /**
   * 수정
   */
  const handleEdit = async (type: DiscipleType) => {
    try {
      if (!type.target || type.target === 0) {
        toast.error('목표치를 입력해주세요.');
        return;
      }

      spinner.start();

      // DB에 저장
      const updatedItem = await updateDiscipleType(type);

      // DB에서 전체 리스트 다시 가져오기 (DB 순서대로 표시)
      const updatedTypes = await getDiscipleTypes();
      setTypes(updatedTypes);

      // 수정된 항목 하이라이트 (반환된 항목의 ID 사용)
      if (updatedItem && updatedItem.id) {
        setTimeout(() => {
          setHighlightedId(updatedItem.id);
        }, 100);
      }

      toast.success('수정되었습니다.');
    } catch (error) {
      console.error(error);
      toast.error(error.message || '수정에 실패했습니다.');
    } finally {
      spinner.stop();
    }
  };

  /**
   * 삭제
   */
  const handleDelete = async (id: number) => {
    try {
      spinner.start();

      // DB에서 삭제
      await deleteDiscipleType(id);

      // DB에서 전체 리스트 다시 가져오기 (DB 순서대로 표시)
      const updatedTypes = await getDiscipleTypes();
      setTypes(updatedTypes);

      toast.success('삭제되었습니다.');
    } catch (error) {
      console.error(error);
      toast.error(error.message || '삭제에 실패했습니다.');
    } finally {
      spinner.stop();
    }
  };

  /**
   * Input의 값을 변경한다.
   */
  const handleChange = ({ target: { name, value } }, idx) => {
    const updatedTypes = [...types];
    updatedTypes[idx] = {
      ...updatedTypes[idx],
      [name]: name === 'departmentId' || name === 'target' ? (value === '' ? undefined : Number(value)) : value,
    };
    setTypes(updatedTypes);
  };

  return (
    <div id="primary" className="content-area">
      {/* <!--s:#content --> */}
      <div id="content" className="site-content">
        <div>
          <div className="title-inner">
            <img src="/common/images/title.png" alt="" />
            <h1>{categories.find((category) => category.categoryCode === pathname.split('/')[4])?.categoryName}</h1>
          </div>
          <div style={{ height: 20 }}></div>
          <div style={{ clear: 'both' }}></div>
          <div className="title_subpage_solid_1"></div>
          <div style={{ height: 30 }}></div>

          <div className="">
            <table className="tbl_basic">
              <colgroup>
                <col style={{ width: '5%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '8%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>NO.</th>
                  <th>부서</th>
                  <th>제자명</th>
                  <th>설명</th>
                  <th>목표(명)</th>
                  <th>
                    <div>
                      비율(%)
                      <span className={styles.tooltipWrapper} style={{ cursor: 'help', position: 'relative' }}>
                        <Icon icon="mdi:help-circle-outline" width={16} height={16} style={{ color: '#666' }} />
                        <span className={styles.tooltipText}>각 교회의 예배자수 대비 제자 수의 비율(%)로 자동 산출됩니다</span>
                      </span>
                    </div>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {/* 제자종류 목록 */}
                <DiscipleTypeList types={types} onChange={handleChange} onEdit={handleEdit} onDelete={handleDelete} highlightedId={highlightedId} />
                {/* 제자종류 추가 */}
                <DiscipleTypeCreate newType={newType} setNewType={setNewType} onAdd={handleAddType} />
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* <!--//e: #content --> */}
    </div>
  );
};

export default AdminYCMS;
